'use client';

import { cn } from '@bells/core/lib/utils';
import { baseLocale } from '@bells/i18n/runtime';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@bells/ui/components/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@bells/ui/components/select';
import { useSearchParams } from 'next/navigation';
import { type MouseEvent, type ReactNode, useCallback, useMemo } from 'react';

export interface PaginationWithLinksProps {
  pageSizeSelectOptions?: {
    pageSizeSearchParam?: string;
    pageSizeOptions: number[];
  };
  totalCount: number;
  pageSize: number;
  page: number;
  pageSearchParam?: string;
  className?: string;
  useQuery?: boolean;
  url: string;
  onChange?: (
    event: MouseEvent<HTMLAnchorElement>,
    url: string,
    page: number
  ) => void;
}

export function PaginationWithLinks({
  pageSizeSelectOptions,
  pageSize,
  totalCount,
  page,
  className,
  onChange,
  useQuery,
  url,
}: PaginationWithLinksProps) {
  const searchParams = useSearchParams();

  const totalPageCount = Math.ceil(totalCount / pageSize);

  const pathname = useMemo(() => {
    const urlParts = url.split('/');
    if (urlParts.length > 0 && urlParts[1] == baseLocale) {
      const newParts = urlParts.splice(2);
      return ['', ...newParts].join('/');
    } else {
      return urlParts.join('/');
    }
  }, [url]);

  const buildLink = useCallback(
    (newPage: number) => {
      if (useQuery) {
        if (!searchParams) {
          return `${pathname}?page=${newPage}`;
        }
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('page', String(newPage));
        return `${pathname}?${newSearchParams.toString()}`;
      } else {
        const paths = pathname.split('/');
        const index = paths.findIndex((df) => df == 'page');

        if (index <= 0) {
          return `${pathname}/page/${String(newPage)}`;
        } else {
          paths[index + 1] = String(newPage);
          return paths.join('/');
        }
      }
    },
    [searchParams, url]
  );

  const navToPageSize = useCallback(
    (newPageSize: number) => {
      if (useQuery) {
        const key = pageSizeSelectOptions?.pageSizeSearchParam || 'limit';
        const newSearchParams = new URLSearchParams(searchParams || undefined);
        newSearchParams.set(key, String(newPageSize));
        newSearchParams.delete('page');

        return `${pathname}?${newSearchParams.toString()}`;
      } else {
        const paths = pathname.split('/');
        const index = paths.findIndex((df) => df == 'page');
        if (index <= 0) {
          return `${pathname}/page/${String(newPageSize)}`;
        } else {
          paths[index + 1] = String(newPageSize);
          return paths.join('/');
        }
      }
    },
    [searchParams, pathname]
  );

  const renderPageNumbers = () => {
    const items: ReactNode[] = [];
    const maxVisiblePages = 5;

    if (totalPageCount <= maxVisiblePages) {
      for (let i = 1; i <= totalPageCount; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              onClick={(e) => {
                onChange?.(e, buildLink(i), i);
              }}
              href={buildLink(i)}
              isActive={page === i}
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
    } else {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink
            onClick={(e) => {
              onChange?.(e, buildLink(1), 1);
            }}
            href={buildLink(1)}
            isActive={page === 1}
          >
            1
          </PaginationLink>
        </PaginationItem>
      );

      if (page > 3) {
        items.push(
          <PaginationItem key='ellipsis-start'>
            <PaginationEllipsis />
          </PaginationItem>
        );
      }

      const start = Math.max(2, page - 1);
      const end = Math.min(totalPageCount - 1, page + 1);

      for (let i = start; i <= end; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              href={buildLink(i)}
              onClick={(e) => {
                onChange?.(e, buildLink(i), i);
              }}
              isActive={page === i}
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }

      if (page < totalPageCount - 2) {
        items.push(
          <PaginationItem key='ellipsis-end'>
            <PaginationEllipsis />
          </PaginationItem>
        );
      }

      items.push(
        <PaginationItem key={totalPageCount}>
          <PaginationLink
            onClick={(e) => {
              onChange?.(e, buildLink(totalPageCount), totalPageCount);
            }}
            href={buildLink(totalPageCount)}
            isActive={page === totalPageCount}
          >
            {totalPageCount}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return items;
  };

  return (
    <div
      className={cn(
        'inline-flex flex-col flex-wrap items-center gap-3 md:flex-row',
        className
      )}
    >
      {pageSizeSelectOptions && (
        <div className='flex flex-1 flex-col gap-4'>
          <SelectRowsPerPage
            options={pageSizeSelectOptions.pageSizeOptions}
            setPageSize={navToPageSize}
            pageSize={pageSize}
          />
        </div>
      )}
      <Pagination
        className={cn('flex-wrap', { 'md:justify-end': pageSizeSelectOptions })}
      >
        <PaginationContent className='flex-wrap max-sm:gap-0'>
          {!(page === 1) && (
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => {
                  onChange?.(
                    e,
                    buildLink(Math.max(page - 1, 1)),
                    Math.max(page - 1, 1)
                  );
                }}
                href={buildLink(Math.max(page - 1, 1))}
                tabIndex={page === 1 ? -1 : undefined}
                className={
                  page === 1 ? 'pointer-events-none opacity-50' : undefined
                }
              />
            </PaginationItem>
          )}
          {renderPageNumbers()}
          {!(page === totalPageCount || totalPageCount <= 0) && (
            <PaginationItem>
              <PaginationNext
                onClick={(e) => {
                  onChange?.(
                    e,
                    buildLink(Math.min(page + 1, totalPageCount)),
                    Math.min(page + 1, totalPageCount)
                  );
                }}
                href={buildLink(Math.min(page + 1, totalPageCount))}
                tabIndex={page === totalPageCount ? -1 : undefined}
                className={
                  page === totalPageCount || totalPageCount <= 0
                    ? 'pointer-events-none opacity-50'
                    : undefined
                }
              />
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
    </div>
  );
}

function SelectRowsPerPage({
  options,
  setPageSize,
  pageSize,
}: {
  options: number[];
  setPageSize: (newSize: number) => void;
  pageSize: number;
}) {
  return (
    <div className='flex items-center gap-4'>
      <span className='whitespace-nowrap text-sm'>Rows per page</span>

      <Select
        value={String(pageSize)}
        onValueChange={(value) => setPageSize(Number(value))}
      >
        <SelectTrigger>
          <SelectValue placeholder='Select page size'>
            {String(pageSize)}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={String(option)}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
